"use client";

import { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import config from "@/config/app";
import pricingService from "@/services/pricing";

interface FooterProps {
  initialOpenSection?: "contact" | "refunds" | "terms" | "privacy" | null;
}

export default function Footer({
  initialOpenSection = null,
}: Readonly<FooterProps>) {
  const { t, lang } = useI18n();
  const [openSection, setOpenSection] = useState(initialOpenSection);
  const [previousInitialSection, setPreviousInitialSection] = useState(initialOpenSection);
  if (initialOpenSection !== previousInitialSection) {
    setPreviousInitialSection(initialOpenSection);
    if (initialOpenSection) setOpenSection(initialOpenSection);
  }
  const showContact = openSection === "contact";
  const showRefunds = openSection === "refunds";
  const showTerms = openSection === "terms";
  const showPrivacy = openSection === "privacy";
  const [standardDeliveryMinutes, setStandardDeliveryMinutes] = useState(120);
  const deliveryTime = (() => {
    const hours = Math.floor(standardDeliveryMinutes / 60);
    const minutes = standardDeliveryMinutes % 60;
    const isEnglish = lang === "en-US";
    if (hours && minutes) return isEnglish ? `${hours} ${hours === 1 ? "hour" : "hours"} and ${minutes} minutes` : `${hours} ${hours === 1 ? "hora" : "horas"} e ${minutes} minutos`;
    if (hours) return isEnglish ? `${hours} ${hours === 1 ? "hour" : "hours"}` : `${hours} ${hours === 1 ? "hora" : "horas"}`;
    return isEnglish ? `${minutes} minutes` : `${minutes} minutos`;
  })();

  useEffect(() => {
    void pricingService.getPricingConfig().then((pricing) => {
      if (typeof pricing.standard_delivery_minutes === "number") {
        setStandardDeliveryMinutes(pricing.standard_delivery_minutes);
      }
    });
  }, []);
  const supportContact = config.support;
  const isWhatsappSupport = supportContact.channel === "whatsapp";
  const supportLabel = isWhatsappSupport ? "WhatsApp" : "Instagram";
  const supportHandle = isWhatsappSupport
    ? supportContact.whatsappDisplay
    : supportContact.instagramHandle;
  const supportUrl = isWhatsappSupport
    ? supportContact.whatsappUrl
    : `https://instagram.com/${supportContact.instagramHandle.replace(/^@/, "")}`;
  const supportDescription = isWhatsappSupport
    ? t(
        "footer.contact.desc.whatsapp",
        "Nossa equipe está disponível para esclarecer dúvidas, auxiliar com pedidos e oferecer suporte personalizado. Fale com a gente pelo WhatsApp e receba atendimento humanizado.",
      )
    : t(
        "footer.contact.desc",
        "Nossa equipe está disponível para esclarecer dúvidas, auxiliar com pedidos e oferecer suporte personalizado. Fale com a gente pelo Direct no Instagram e receba atendimento humanizado.",
      );
  const refundContactPrefix = isWhatsappSupport
    ? t(
        "footer.refunds.how.desc.prefix.whatsapp",
        "Entre em contato conosco pelo WhatsApp ( ",
      )
    : t(
        "footer.refunds.how.desc.prefix",
        "Entre em contato conosco pelo nosso Instagram em Direct ( ",
      );
  const termsSupportPrefix = isWhatsappSupport
    ? t(
        "footer.terms.support.desc.prefix.whatsapp",
        "Para dúvidas, suporte ou solicitações relacionadas ao serviço, envie uma mensagem pelo WhatsApp",
      )
    : t(
        "footer.terms.support.desc.prefix",
        "Para dúvidas, suporte ou solicitações relacionadas ao serviço, envie uma mensagem por Direct no Instagram",
      );
  const privacyContactIntro = isWhatsappSupport
    ? t(
        "footer.privacy.contact.intro.whatsapp",
        "Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato via WhatsApp:",
      )
    : t(
        "footer.privacy.contact.intro",
        "Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato via Direct no Instagram:",
      );

  // Efeito para abrir seção baseada na prop inicial
  useEffect(() => {
    if (initialOpenSection) {
      // Scroll para o footer após um pequeno delay para garantir que a seção seja renderizada
      const timer = setTimeout(() => {
        const footerElement = document.querySelector("footer");
        if (footerElement) {
          footerElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [initialOpenSection]);

  const toggleSection = (
    section: "contact" | "refunds" | "terms" | "privacy",
  ) => {
    setOpenSection((current) => current === section ? null : section);
  };

  return (
    <footer
      className="relative w-full"
      style={{
        background: "#ffffff",
        borderTop: "2px solid rgba(255,255,255,0.06)",
        position: "relative",
      }}
    >
      <div className="container mx-auto px-4 py-6">
        {/* Menu Principal */}
        <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
          {/* Botão Contato */}
          <button
            onClick={() => toggleSection("contact")}
            className="group relative px-6 py-3 text-lg font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg tracking-wide font-subtitle"
            style={{
              background:
                "linear-gradient(to bottom, #059669, #047857, #065f46)",
              border: "3px solid #34d399",
              color: "#ffffff",
              textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              boxShadow: "0 0 20px rgba(16, 185, 129, 0.5)",
            }}
          >
            <span className="flex items-center space-x-2">
              <span className="text-xl">📞</span>
              <span>{t("footer.menu.contact", "Atendimento ao Cliente")}</span>
            </span>
          </button>

          {/* Botão Política de Reembolsos */}
          <button
            onClick={() => toggleSection("refunds")}
            className="group relative px-6 py-3 text-lg font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg tracking-wide font-subtitle"
            style={{
              background:
                "linear-gradient(to bottom, #059669, #047857, #065f46)",
              border: "3px solid #34d399",
              color: "#ffffff",
              textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              boxShadow: "0 0 20px rgba(16, 185, 129, 0.5)",
            }}
          >
            <span className="flex items-center space-x-2">
              <span className="text-xl">💰</span>
              <span>{t("footer.menu.refunds", "Política de Reembolsos")}</span>
            </span>
          </button>

          {/* Botão Termos e Condições */}
          <button
            onClick={() => toggleSection("terms")}
            className="group relative px-6 py-3 text-lg font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg tracking-wide font-subtitle"
            style={{
              background:
                "linear-gradient(to bottom, #059669, #047857, #065f46)",
              border: "3px solid #34d399",
              color: "#ffffff",
              textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              boxShadow: "0 0 20px rgba(16, 185, 129, 0.5)",
            }}
          >
            <span className="flex items-center space-x-2">
              <span className="text-xl">📋</span>
              <span>{t("footer.menu.terms", "Termos e Condições")}</span>
            </span>
          </button>

          {/* Botão Política de Privacidade */}
          <button
            onClick={() => toggleSection("privacy")}
            className="group relative px-6 py-3 text-lg font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg tracking-wide font-subtitle"
            style={{
              background:
                "linear-gradient(to bottom, #059669, #047857, #065f46)",
              border: "3px solid #34d399",
              color: "#ffffff",
              textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
              boxShadow: "0 0 20px rgba(16, 185, 129, 0.5)",
            }}
          >
            <span className="flex items-center space-x-2">
              <span className="text-xl">🔒</span>
              <span>{t("footer.menu.privacy", "Política de Privacidade")}</span>
            </span>
          </button>
        </div>

        {/* Seções Expandidas */}
        {/* Seção de Contato */}
        {showContact && (
          <div
            className="mt-6 rounded-2xl p-6 shadow-lg"
            style={{
              background:
                "linear-gradient(135deg,#fdf2f2 0%,#ffffff 55%, #f8d1d1 100%)",
              border: "1px solid rgba(232,115,115,0.3)",
              boxShadow: "0 8px 25px rgba(153,0,0,0.25)",
            }}
          >
            <div className="text-center">
              <h3
                className="text-2xl font-bold tracking-wide font-title mb-4"
                style={{
                  color: "#800000",
                  textShadow: "0 2px 6px rgba(0,0,0,0.25)",
                }}
              >
                {t("footer.contact.title", "✨ Atendimento ao Cliente ✨")}
              </h3>

              <div className="space-y-4">
                <div className="flex flex-col md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-4">
                  <span
                    className="text-lg font-semibold font-subtitle"
                    style={{ color: "#4a2c2c" }}
                  >
                    {supportLabel}:
                  </span>
                  <a
                    href={supportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-4 py-2 text-lg font-bold rounded-lg transition-all duration-300 transform hover:scale-105 font-subtitle"
                    style={{
                      background:
                        "linear-gradient(to bottom, #059669, #047857, #065f46)",
                      border: "2px solid #34d399",
                      color: "#ffffff",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.4)",
                    }}
                  >
                    <span className="text-xl">
                      {isWhatsappSupport ? "📱" : "📸"}
                    </span>
                    <span>{supportHandle}</span>
                  </a>
                </div>

                <p
                  className="text-base font-subtitle italic max-w-2xl mx-auto leading-relaxed"
                  style={{ color: "#4a2c2c" }}
                >
                  {supportDescription}
                </p>

                <div
                  className="text-sm mt-3 font-subtitle"
                  style={{ color: "#4a2c2c" }}
                >
                  <strong>
                    {t("footer.contact.hours.title", "Horário de Atendimento:")}
                  </strong>{" "}
                  {t(
                    "footer.contact.hours.value",
                    "Segunda a Sexta, das 9h às 18h",
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Seção de Política de Reembolsos */}
        {showRefunds && (
          <div
            className="mt-6 rounded-2xl p-6 shadow-lg"
            style={{
              background:
                "linear-gradient(135deg,#fdf2f2 0%,#ffffff 55%, #f8d1d1 100%)",
              border: "1px solid rgba(232,115,115,0.3)",
              boxShadow: "0 8px 25px rgba(153,0,0,0.25)",
            }}
          >
            <div className="text-center max-w-4xl mx-auto">
              <h3
                className="text-2xl font-bold tracking-wide font-title mb-6"
                style={{
                  color: "#800000",
                  textShadow: "0 2px 6px rgba(0,0,0,0.25)",
                }}
              >
                {t(
                  "footer.refunds.title",
                  "💰 Política de Reembolsos e Contestações 💰",
                )}
              </h3>

              <div className="text-left space-y-4">
                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4
                    className="text-lg font-bold mb-2 font-title"
                    style={{ color: "#660000" }}
                  >
                    {t(
                      "footer.refunds.when.title",
                      "🎯 Quando o Reembolso é Aplicável",
                    )}
                  </h4>
                  <p
                    className="text-sm font-subtitle leading-relaxed"
                    style={{ color: "#4a2c2c" }}
                  >
                    {t(
                      "footer.refunds.when.desc",
                      "O reembolso integral será concedido nas seguintes situações:",
                    )}
                  </p>
                  <ul
                    className="text-sm font-subtitle mt-2 space-y-1"
                    style={{ color: "#4a2c2c" }}
                  >
                    <li>
                      •{" "}
                      {t(
                        "footer.refunds.when.items.delivery_time",
                        "Não entrega da carta canalizada no prazo estabelecido (até {time})",
                      ).replace("{time}", deliveryTime)}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.refunds.when.items.technical_failure",
                        "Falha técnica que impeça a geração ou envio da carta",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.refunds.when.items.payment_error",
                        "Erro no processamento do pagamento que resulte em cobrança duplicada",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.refunds.when.items.system_problems",
                        "Problemas técnicos em nossos sistemas que impeçam a prestação do serviço",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4
                    className="text-lg font-bold mb-2 font-title"
                    style={{ color: "#660000" }}
                  >
                    {t(
                      "footer.refunds.deadline.title",
                      "⏰ Prazo para Processamento",
                    )}
                  </h4>
                  <p
                    className="text-sm font-subtitle leading-relaxed"
                    style={{ color: "#4a2c2c" }}
                  >
                    {t(
                      "footer.refunds.deadline.desc",
                      "Após a aprovação da solicitação de reembolso, o valor será devolvido em até 72 horas úteis para o mesmo método de pagamento utilizado na compra.",
                    )}
                  </p>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4
                    className="text-lg font-bold mb-2 font-title"
                    style={{ color: "#660000" }}
                  >
                    {t(
                      "footer.refunds.how.title",
                      "📝 Como Solicitar o Reembolso",
                    )}
                  </h4>
                  <p
                    className="text-sm font-subtitle leading-relaxed"
                    style={{ color: "#4a2c2c" }}
                  >
                    {refundContactPrefix}
                    <strong>{supportHandle}</strong>
                    {t("footer.refunds.how.desc.suffix", " ) informando:")}
                  </p>
                  <ul
                    className="text-sm font-subtitle mt-2 space-y-1"
                    style={{ color: "#4a2c2c" }}
                  >
                    <li>
                      •{" "}
                      {t(
                        "footer.refunds.how.item.reason",
                        "Motivo da solicitação de reembolso",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.refunds.how.item.order_date",
                        "Data da solicitação do pedido",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.refunds.how.item.whatsapp",
                        "Número de WhatsApp informado no pedido",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.refunds.how.item.email",
                        "Email informado no pedido",
                      )}
                    </li>
                  </ul>
                </div>

                <div
                  className="rounded-lg p-4 border-2"
                  style={{
                    background: "rgba(254,243,199,0.5)",
                    borderColor: "rgba(245,158,11,0.3)",
                  }}
                >
                  <h4
                    className="text-lg font-bold mb-2 font-title"
                    style={{ color: "#660000" }}
                  >
                    {t("footer.refunds.important.title", "⚠️ Importante")}
                  </h4>
                  <p
                    className="text-sm leading-relaxed font-subtitle font-normal"
                    style={{ color: "#4a2c2c" }}
                  >
                    {t(
                      "footer.refunds.important.desc",
                      "Este é um serviço esotérico e espiritual. O reembolso não se aplica em casos de insatisfação com o conteúdo da carta canalizada, uma vez que se trata de uma interpretação energética personalizada. Nosso compromisso é com a entrega do serviço conforme descrito.",
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Seção de Termos e Condições */}
        {showTerms && (
          <div
            className="mt-6 rounded-2xl p-6 shadow-lg"
            style={{
              background:
                "linear-gradient(135deg,#fdf2f2 0%,#ffffff 55%, #f8d1d1 100%)",
              border: "1px solid rgba(232,115,115,0.3)",
              boxShadow: "0 8px 25px rgba(153,0,0,0.25)",
            }}
          >
            <div className="text-center max-w-4xl mx-auto">
              <h3
                className="text-2xl font-bold tracking-wide font-title mb-6"
                style={{
                  color: "#800000",
                  textShadow: "0 2px 6px rgba(0,0,0,0.25)",
                }}
              >
                {t("footer.terms.title", "📋 Termos e Condições de Uso 📋")}
              </h3>

              <div className="text-left space-y-4">
                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4
                    className="text-lg font-bold mb-2 font-title"
                    style={{ color: "#660000" }}
                  >
                    {t("footer.terms.nature.title", "🔮 Natureza do Serviço")}
                  </h4>
                  <p className="text-[#4a2c2c] text-sm font-subtitle leading-relaxed">
                    {t(
                      "footer.terms.nature.desc",
                      "O serviço de “Cartas Canalizadas” é uma prática esotérica e espiritual que visa oferecer orientações através da interpretação de cartas de tarot. Este serviço tem finalidade de entretenimento, reflexão pessoal e orientação espiritual, não substituindo aconselhamento médico, psicológico, jurídico ou financeiro profissional. A formatação e refinamento final do texto podem contar com apoio de tecnologias de Inteligência Artificial, sem modificar a essência espiritual da interpretação realizada.",
                    )}
                  </p>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.terms.acceptance.title",
                      "✅ Aceitação dos Termos",
                    )}
                  </h4>
                  <p className="text-[#4a2c2c] text-sm font-subtitle leading-relaxed">
                    {t(
                      "footer.terms.acceptance.desc",
                      "Ao utilizar nosso serviço, você concorda com estes termos e condições. O pagamento confirma sua aceitação integral destes termos. Caso não concorde, não utilize o serviço.",
                    )}
                  </p>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t("footer.terms.delivery.title", "📱 Entrega e Prazo")}
                  </h4>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1 font-title">
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.delivery.items.delivery_time",
                        "A carta canalizada será entregue via WhatsApp e/ou email em até {time} após confirmação do pagamento",
                      ).replace("{time}", deliveryTime)}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.delivery.items.pdf_format",
                        "O conteúdo será enviado em formato PDF personalizado",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.delivery.items.valid_contacts",
                        "É necessário fornecer número de WhatsApp e email válidos para recebimento",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.delivery.items.not_responsible",
                        "Não nos responsabilizamos por informações incorretas fornecidas pelo cliente",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t("footer.terms.privacy.title", "🔒 Privacidade e Dados")}
                  </h4>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1 font-title">
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.privacy.items.confidentiality",
                        "Todas as informações fornecidas são tratadas com confidencialidade",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.privacy.items.no_share",
                        "Não compartilhamos dados pessoais com terceiros",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.privacy.items.only_service",
                        "Os dados são utilizados exclusivamente para prestação do serviço",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.privacy.items.deletion_request",
                        "Você pode solicitar exclusão dos seus dados pelo WhatsApp",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.terms.limitations.title",
                      "⚖️ Limitações e Responsabilidades",
                    )}
                  </h4>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1 font-title">
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.limitations.items.no_guarantee",
                        "O serviço não garante resultados específicos ou mudanças na vida",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.limitations.items.esoteric_practice",
                        "As interpretações são baseadas em práticas esotéricas tradicionais",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.limitations.items.not_responsible_decisions",
                        "Não nos responsabilizamos por decisões tomadas com base no conteúdo das cartas",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.limitations.items.adults_only",
                        "O serviço é destinado a maiores de 18 anos",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.limitations.items.not_recommended_severe",
                        "Não recomendamos para pessoas em situação de vulnerabilidade emocional severa",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t("footer.terms.payment.title", "💳 Pagamento e Cobrança")}
                  </h4>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1 font-title">
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.payment.items.secure_mp",
                        "O pagamento é processado com segurança total via MercadoPago",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.payment.items.charged_upfront",
                        "O valor é cobrado integralmente no momento da confirmação",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.payment.items.no_extra_fees",
                        "Não há taxas adicionais ou cobranças recorrentes",
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "footer.terms.payment.items.single_use",
                        "O serviço é de uso único por pedido",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-[rgba(254,243,199,0.5)] rounded-lg p-4 border-2 border-[rgba(245,158,11,0.3)]">
                  <h4 className="text-lg font-bold text-[#4a2c2c] mb-2 font-title">
                    {t("footer.terms.support.title", "📞 Suporte e Contato")}
                  </h4>
                  <p className="text-[#4a2c2c] text-sm font-subtitle space-y-1 font-title">
                    {termsSupportPrefix} <strong>{supportHandle}</strong>.{" "}
                    {t(
                      "footer.terms.support.desc.suffix",
                      "Nossa equipe está disponível de segunda a sexta, das 9h às 18h.",
                    )}
                  </p>
                </div>

                <div className="bg-[rgba(254,226,226,0.5)] rounded-lg p-4 border-2 border-[rgba(220,38,38,0.3)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.terms.legal_notice.title",
                      "⚠️ Aviso Legal Importante",
                    )}
                  </h4>
                  <p className="text-[#660000] text-sm font-subtitle space-y-1 font-title">
                    {t(
                      "footer.terms.legal_notice.text",
                      "Este serviço é oferecido para fins de entretenimento e orientação espiritual. Não substitui consultas médicas, psicológicas, jurídicas ou financeiras profissionais. Em caso de problemas de saúde mental ou emocional, procure ajuda profissional qualificada.",
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t-2 border-[rgba(232,115,115,0.2)] text-center">
                <p className="text-sm text-[#4a2c2c] italic font-title">
                  {t("footer.terms.last_update", "Última atualização:")}{" "}
                  {new Date().toLocaleDateString("pt-BR")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Seção de Política de Privacidade */}
        {showPrivacy && (
          <div
            className="mt-6 rounded-2xl p-6 shadow-lg"
            style={{
              background:
                "linear-gradient(135deg,#fdf2f2 0%,#ffffff 55%, #f8d1d1 100%)",
              border: "1px solid rgba(232,115,115,0.3)",
              boxShadow: "0 8px 25px rgba(153,0,0,0.25)",
            }}
          >
            <div className="text-center max-w-4xl mx-auto">
              <h3
                className="text-2xl font-bold tracking-wide font-title mb-6"
                style={{
                  color: "#800000",
                  textShadow: "0 2px 6px rgba(0,0,0,0.25)",
                }}
              >
                {t(
                  "footer.privacy.title",
                  "🔒 Política de Privacidade e Proteção de Dados 🔒",
                )}
              </h3>

              <div className="text-left space-y-4">
                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.privacy.collect.title",
                      "📋 Informações que Coletamos",
                    )}
                  </h4>
                  <p className="text-[#4a2c2c] text-sm font-subtitle leading-relaxed mb-2">
                    {t(
                      "footer.privacy.collect.intro",
                      "Para prestar o serviço de cartas canalizadas, coletamos as seguintes informações:",
                    )}
                  </p>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1">
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.collect.labels.personal",
                          "Dados pessoais",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.collect.desc.personal",
                        "Nome completo, data de nascimento, signo zodiacal",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t("footer.privacy.collect.labels.contact", "Contato")}:
                      </strong>{" "}
                      {t(
                        "footer.privacy.collect.desc.contact",
                        "Número de WhatsApp e email para entrega da carta",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.collect.labels.relationship",
                          "Informações do relacionamento",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.collect.desc.relationship",
                        "Status, frequência de contato, estilo de comunicação",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.collect.labels.questions",
                          "Perguntas específicas",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.collect.desc.questions",
                        "Questões que deseja abordar na consulta",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.collect.labels.payment",
                          "Dados de pagamento",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.collect.desc.payment",
                        "Informações processadas com MercadoPago (não armazenamos dados de cartão)",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.privacy.usage.title",
                      "🎯 Como Utilizamos suas Informações",
                    )}
                  </h4>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1">
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.usage.labels.service",
                          "Prestação do serviço",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.usage.desc.service",
                        "Criação personalizada da sua carta canalizada",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t("footer.privacy.usage.labels.delivery", "Entrega")}:
                      </strong>{" "}
                      {t(
                        "footer.privacy.usage.desc.delivery",
                        "Envio da carta via WhatsApp e email no número fornecido",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t("footer.privacy.usage.labels.support", "Suporte")}:
                      </strong>{" "}
                      {t(
                        "footer.privacy.usage.desc.support",
                        "Atendimento ao cliente e resolução de questões",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.usage.labels.payments",
                          "Processamento de pagamentos",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.usage.desc.payments",
                        "Confirmação e gestão de transações",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.usage.labels.improvement",
                          "Melhoria do serviço",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.usage.desc.improvement",
                        "Análise estatística para aperfeiçoamento (dados anonimizados)",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.privacy.protection.title",
                      "🛡️ Proteção e Segurança dos Dados",
                    )}
                  </h4>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1">
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.protection.labels.encryption",
                          "Criptografia",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.protection.desc.encryption",
                        "Todas as comunicações são protegidas por SSL/TLS",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.protection.labels.restricted_access",
                          "Acesso restrito",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.protection.desc.restricted_access",
                        "Apenas equipe autorizada pode acessar seus dados",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.protection.labels.secure_servers",
                          "Servidores seguros",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.protection.desc.secure_servers",
                        "Dados armazenados em infraestrutura protegida",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.protection.labels.payments",
                          "Pagamentos",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.protection.desc.payments",
                        "Processados por provedor certificado PCI DSS (MercadoPago)",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.protection.labels.backups",
                          "Backups",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.protection.desc.backups",
                        "Realizados com criptografia para garantir disponibilidade",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.privacy.share.title",
                      "🤝 Compartilhamento de Dados",
                    )}
                  </h4>
                  <p className="text-[#4a2c2c] text-sm font-subtitle leading-relaxed mb-2">
                    <strong>
                      {t(
                        "footer.privacy.share.no_sell",
                        "Não vendemos, alugamos ou compartilhamos seus dados pessoais",
                      )}
                    </strong>{" "}
                    {t(
                      "footer.privacy.share.post_strong",
                      "com terceiros para fins comerciais. Seus dados podem ser compartilhados apenas nos seguintes casos:",
                    )}
                  </p>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1">
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.share.labels.providers",
                          "Prestadores de serviço",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.share.desc.providers",
                        "Provedor de pagamentos (MercadoPago), serviços de cloud (Google)",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.share.labels.legal",
                          "Obrigações legais",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.share.desc.legal",
                        "Quando exigido por lei ou autoridades competentes",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.share.labels.rights",
                          "Proteção de direitos",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.share.desc.rights",
                        "Para proteger nossos direitos legítimos ou dos usuários",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.privacy.retention.title",
                      "⏰ Retenção de Dados",
                    )}
                  </h4>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1">
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.retention.labels.consultation",
                          "Dados da consulta",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.retention.desc.consultation",
                        "Mantidos por até 15 dias para suporte e histórico",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.retention.labels.payment",
                          "Dados de pagamento",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.retention.desc.payment",
                        "Retidos conforme exigências fiscais (5 anos)",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.retention.labels.logs",
                          "Logs de sistema",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.retention.desc.logs",
                        "Mantidos por 6 meses para fins de segurança",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.retention.labels.deletion",
                          "Exclusão",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.retention.desc.deletion",
                        "Dados removidos automaticamente após os prazos ou mediante solicitação",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.privacy.rights.title",
                      "👤 Seus Direitos (LGPD)",
                    )}
                  </h4>
                  <p className="text-[#4a2c2c] text-sm font-subtitle leading-relaxed mb-2">
                    {t(
                      "footer.privacy.rights.intro",
                      "Conforme a Lei Geral de Proteção de Dados (LGPD), você tem os seguintes direitos:",
                    )}
                  </p>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1">
                    <li>
                      •{" "}
                      <strong>
                        {t("footer.privacy.rights.labels.access", "Acesso")}:
                      </strong>{" "}
                      {t(
                        "footer.privacy.rights.desc.access",
                        "Confirmar a existência e acessar seus dados",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.rights.labels.correction",
                          "Correção",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.rights.desc.correction",
                        "Solicitar correção de dados incompletos ou incorretos",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t("footer.privacy.rights.labels.deletion", "Exclusão")}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.rights.desc.deletion",
                        "Solicitar eliminação dos dados pessoais",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.rights.labels.portability",
                          "Portabilidade",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.rights.desc.portability",
                        "Solicitar transferência dos dados para outro fornecedor",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.rights.labels.objection",
                          "Oposição",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.rights.desc.objection",
                        "Opor-se ao tratamento dos dados",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.rights.labels.revocation",
                          "Revogação",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.rights.desc.revocation",
                        "Revogar consentimento a qualquer momento",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.privacy.cookies.title",
                      "🍪 Cookies e Tecnologias Similares",
                    )}
                  </h4>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle space-y-1">
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.cookies.labels.essential",
                          "Essenciais",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.cookies.desc.essential",
                        "Necessários para funcionamento básico do site",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.cookies.labels.functional",
                          "Funcionais",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.cookies.desc.functional",
                        "Melhoram a experiência de navegação",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.cookies.labels.analytics",
                          "Análise",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.cookies.desc.analytics",
                        "Permitem analisar uso do site (dados anonimizados)",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.cookies.labels.management",
                          "Gerenciamento",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.cookies.desc.management",
                        "Você pode controlar cookies através do navegador",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-white/50 rounded-lg p-4 border-2 border-[rgba(232,115,115,0.2)]">
                  <h4 className="text-lg font-bold text-[#660000] mb-2 font-title">
                    {t(
                      "footer.privacy.contact.title",
                      "📞 Exercício de Direitos e Contato",
                    )}
                  </h4>
                  <p className="text-[#4a2c2c] text-sm font-subtitle leading-relaxed">
                    {privacyContactIntro}
                  </p>
                  <ul className="text-[#4a2c2c] text-sm font-subtitle mt-2 space-y-1">
                    <li>
                      • <strong>{supportLabel}:</strong>{" "}
                      <a
                        href={supportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-amber-600"
                      >
                        {supportHandle}
                      </a>
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t("footer.privacy.contact.labels.hours", "Horário")}:
                      </strong>{" "}
                      {t(
                        "footer.privacy.contact.hours",
                        "Segunda a Sexta, das 9h às 18h",
                      )}
                    </li>
                    <li>
                      •{" "}
                      <strong>
                        {t(
                          "footer.privacy.contact.labels.response",
                          "Prazo de resposta",
                        )}
                        :
                      </strong>{" "}
                      {t(
                        "footer.privacy.contact.response",
                        "Até 15 dias corridos",
                      )}
                    </li>
                  </ul>
                </div>

                <div className="bg-blue-100 rounded-lg p-4 border-2 border-blue-400">
                  <h4 className="text-lg font-bold text-blue-800 mb-2 font-title">
                    {t(
                      "footer.privacy.legal_base.title",
                      "🔐 Base Legal para Tratamento",
                    )}
                  </h4>
                  <p className="text-blue-800 text-sm font-subtitle space-y-1">
                    {t(
                      "footer.privacy.legal_base.prefix1",
                      "O tratamento dos seus dados é realizado com base no",
                    )}{" "}
                    <strong>
                      {t("footer.privacy.legal_base.b1", "consentimento")}
                    </strong>{" "}
                    {t(
                      "footer.privacy.legal_base.mid1",
                      "fornecido ao utilizar nosso serviço e na",
                    )}{" "}
                    <strong>
                      {t(
                        "footer.privacy.legal_base.b2",
                        "execução do contrato",
                      )}
                    </strong>{" "}
                    {t(
                      "footer.privacy.legal_base.mid2",
                      "de prestação do serviço de cartas canalizadas. Dados fiscais são mantidos para",
                    )}{" "}
                    <strong>
                      {t(
                        "footer.privacy.legal_base.b3",
                        "cumprimento de obrigação legal",
                      )}
                    </strong>
                    .
                  </p>
                </div>

                <div className="bg-[rgba(220,252,231,0.5)] rounded-lg p-4 border-2 border-[rgba(74,222,128,0.3)]">
                  <h4 className="text-lg font-bold text-[#166534] mb-2 font-title">
                    {t("footer.privacy.minors.title", "🌱 Menores de Idade")}
                  </h4>
                  <p className="text-[#166534] text-sm font-subtitle space-y-1">
                    {t(
                      "footer.privacy.minors.prefix",
                      "Nosso serviço é destinado apenas a",
                    )}{" "}
                    <strong>
                      {t("footer.privacy.minors.bold", "maiores de 18 anos")}
                    </strong>
                    .{" "}
                    {t(
                      "footer.privacy.minors.suffix",
                      "Não coletamos intencionalmente dados de menores. Caso identifiquemos dados de menores, eles serão imediatamente removidos do nosso sistema.",
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t-2 border-[rgba(232,115,115,0.2)] text-center">
                <p className="text-sm text-[#4a2c2c] italic font-title">
                  {t("footer.privacy.updated", "Política atualizada em:")}{" "}
                  18/07/2025 {" | "}
                  {t("footer.privacy.version", "Versão")} 1.1
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Copyright */}
        <div className="mt-6 pt-4 border-t-2 border-[rgba(232,115,115,0.2)] text-center">
          <p className="text-sm text-[#4a2c2c] font-title">
            © {new Date().getFullYear()}{" "}
            {t("footer.copyright.company", "Cartas Canalizadas")}.{" "}
            {t("footer.copyright.rights", "Todos os direitos reservados")}.
          </p>
        </div>
      </div>
    </footer>
  );
}
